// Copy + plan-default data for the post-kundali paid-report CTA.
//
// Split out of post-kundali-cta.ts (2026-06-23) purely to keep that module
// under the changed-executable LOC limit (guard G03). This file holds the
// COPY/DATA-heavy blocks — the en/hi headline maps, the per-reason copy
// functions (`copyFor`, `hindiCopyFor`), the secondary-plan copy, the
// pain-first A/B copy, and the canonical PLAN_DEFAULTS price/strike table.
// The pick/routing LOGIC stays in post-kundali-cta.ts and imports from here.
//
// The en/hi (Hinglish) copy is byte-identical to the original (rule 21 — single
// Indian noun + English action word; rule 24 — evergreen prose, no literal
// computed facts). Public entry points (`secondaryPlanCopy`,
// `painFirstVariantCopy`) are re-exported from post-kundali-cta.ts so external
// imports do not churn.

import { premiumReportPlanMap, type ReportDeliverables } from "@ishvaram/domain-data/report-plans";
import { PLAN_ID, type SEOReportPlanId } from "@/lib/seo-intents";

export type PrimaryReportReason =
  | "next_report_param"
  /** The seeker's SEO entry surface carried a dedicated-report intent (career
   * page → career_report; rule 40 never_bury). Distinct from
   * `next_report_param` so the offer impression's `route_rank_reason` makes the
   * entry-intent recovery MEASURABLE in events_raw — the 2026-06-09
   * career_report → ₹0 class was invisible partly because intent picks were
   * indistinguishable from deep-link picks in analytics. */
  | "entry_intent"
  /** The concern the seeker EXPRESSED IN THE CONVERSATION picked the report
   * (chat_concern_match_pilot_2026_07 treatment). Distinct from `entry_intent`
   * so route_rank_reason splits conversation-matched picks from entry-surface
   * picks in events_raw — the pilot readout depends on the distinction. */
  | "chat_concern"
  | "dosha_mangal"
  | "dosha_pitra"
  | "dosha_sade_sati"
  | "dosha_kaal_sarp"
  | "default_brihat";

function canonicalPriceInr(planId: SEOReportPlanId): number {
  return premiumReportPlanMap[planId].price_inr;
}

export const PLAN_DEFAULTS: Record<SEOReportPlanId, { priceInr: number; strikePriceInr?: number }> =
  {
    [PLAN_ID.brihat_kundali]: {
      priceInr: canonicalPriceInr(PLAN_ID.brihat_kundali),
      strikePriceInr: 999,
    },
    [PLAN_ID.marriage_report]: {
      priceInr: canonicalPriceInr(PLAN_ID.marriage_report),
      strikePriceInr: 499,
    },
    [PLAN_ID.career_report]: {
      priceInr: canonicalPriceInr(PLAN_ID.career_report),
      strikePriceInr: 499,
    },
    [PLAN_ID.annual_forecast]: {
      priceInr: canonicalPriceInr(PLAN_ID.annual_forecast),
      strikePriceInr: 499,
    },
    [PLAN_ID.sade_sati]: {
      priceInr: canonicalPriceInr(PLAN_ID.sade_sati),
      strikePriceInr: 499,
    },
    [PLAN_ID.pitra_dosh]: {
      priceInr: canonicalPriceInr(PLAN_ID.pitra_dosh),
      strikePriceInr: 499,
    },
    [PLAN_ID.mahadasha_forecast]: {
      priceInr: canonicalPriceInr(PLAN_ID.mahadasha_forecast),
      strikePriceInr: 499,
    },
    [PLAN_ID.muhurat_report]: {
      priceInr: canonicalPriceInr(PLAN_ID.muhurat_report),
      strikePriceInr: 999,
    },
    [PLAN_ID.dosha_remedy_plan]: {
      priceInr: canonicalPriceInr(PLAN_ID.dosha_remedy_plan),
      strikePriceInr: 999,
    },
    [PLAN_ID.numerology_report]: {
      priceInr: canonicalPriceInr(PLAN_ID.numerology_report),
      strikePriceInr: 499,
    },
    [PLAN_ID.gemstone_report]: {
      priceInr: canonicalPriceInr(PLAN_ID.gemstone_report),
      strikePriceInr: 599,
    },
    [PLAN_ID.baby_name_report]: {
      priceInr: canonicalPriceInr(PLAN_ID.baby_name_report),
      strikePriceInr: 499,
    },
    [PLAN_ID.lal_kitab_report]: {
      priceInr: canonicalPriceInr(PLAN_ID.lal_kitab_report),
      strikePriceInr: 499,
    },
    [PLAN_ID.transit_forecast]: {
      priceInr: canonicalPriceInr(PLAN_ID.transit_forecast),
      strikePriceInr: 499,
    },
  };

const NEXT_REPORT_HEADLINES: Partial<Record<SEOReportPlanId, string>> = {
  [PLAN_ID.brihat_kundali]: "Your Brihat Kundali — ready",
  [PLAN_ID.marriage_report]: "Your Marriage Report — ready",
  [PLAN_ID.career_report]: "Your Career Report — ready",
  [PLAN_ID.annual_forecast]: "Your Annual Forecast — ready",
  [PLAN_ID.sade_sati]: "Your Sade Sati Report — ready",
  [PLAN_ID.pitra_dosh]: "Your Pitra Dosh Report — ready",
  [PLAN_ID.mahadasha_forecast]: "Your Mahadasha Forecast — ready",
  [PLAN_ID.muhurat_report]: "Your Muhurat Report — ready",
  [PLAN_ID.dosha_remedy_plan]: "Your Dosha Remedy Plan — ready",
};

const HINDI_NEXT_REPORT_HEADLINES: Partial<Record<SEOReportPlanId, string>> = {
  [PLAN_ID.brihat_kundali]: "Brihat Kundali Report ready hai",
  [PLAN_ID.marriage_report]: "Marriage Report ready hai",
  [PLAN_ID.career_report]: "Career Report ready hai",
  [PLAN_ID.annual_forecast]: "Annual Forecast ready hai",
  [PLAN_ID.sade_sati]: "Sade Sati Report ready hai",
  [PLAN_ID.pitra_dosh]: "Pitra Dosh Report ready hai",
  [PLAN_ID.mahadasha_forecast]: "Mahadasha Forecast ready hai",
  [PLAN_ID.muhurat_report]: "Muhurat Report ready hai",
  [PLAN_ID.dosha_remedy_plan]: "Dosha Remedy Plan ready hai",
};

export function copyFor(
  planId: SEOReportPlanId,
  reason: PrimaryReportReason,
  doshaName?: string,
  language?: string
): { headline: string; subhead: string; reasonChip: string } {
  const severityNote = doshaName ? `Your chart shows ${doshaName}.` : "";
  if (language === "hi") {
    return hindiCopyFor(planId, reason, doshaName);
  }

  switch (reason) {
    case "next_report_param":
      return {
        headline: NEXT_REPORT_HEADLINES[planId] ?? "Your Annual Forecast — ready",
        subhead:
          "Birth chart generated. The report you came to read is one tap away — payment, then PDF in under 2 minutes.",
        reasonChip: "Continue from where you left",
      };
    case "entry_intent":
      return {
        headline: NEXT_REPORT_HEADLINES[planId] ?? "Your report — matched to your search",
        subhead:
          "Birth chart generated. This is the report matched to what you came looking for — payment, then PDF in under 2 minutes.",
        reasonChip: "Matched to what you came for",
      };
    case "chat_concern":
      return {
        headline: NEXT_REPORT_HEADLINES[planId] ?? "Your report — matched to your question",
        subhead:
          "This is the report matched to what you just asked — the full chart read behind it, payment, then PDF in under 2 minutes.",
        reasonChip: "Matched to what you asked",
      };
    case "dosha_mangal":
      return {
        headline: "Dosha Remedy Plan — your Mangal-specific upay",
        subhead: `${severityNote} The Dosha Remedy Plan checks severity, cancellation, and the upay sequence matched to your graha before you overdo any remedy.`,
        reasonChip: "Recommended for your chart",
      };
    case "dosha_pitra":
      return {
        headline: "Pitra Dosh Report — matched to your 9th house",
        subhead: `${severityNote} The Pitra Dosh Report checks the Sun, 9th house, Rahu/Ketu, cancellation, and the ancestral upay sequence that fits your chart.`,
        reasonChip: "Recommended for your chart",
      };
    case "dosha_sade_sati":
      return {
        headline: "Sade Sati Report — Shani timing for your chart",
        subhead: `${severityNote} Sade Sati phase, natal Shani, current mahadasha, and pressure areas read together — which upay actually fit Shani.`,
        reasonChip: "Recommended for your chart",
      };
    case "dosha_kaal_sarp":
      return {
        headline: "Dosha Remedy Plan — full Kaal Sarp read",
        subhead: `${severityNote} Kaal Sarp affects every house in the chain. The plan checks severity, cancellation, and the upay sequence before any shanti is recommended.`,
        reasonChip: "Recommended for your chart",
      };
    case "default_brihat":
    default:
      return {
        headline: "Brihat Kundali — your full chart read",
        subhead:
          "All 12 bhavs, mahadasha + antardasha timeline, yogas and doshas, and the upay matched to your specific graha — delivered as a readable PDF.",
        reasonChip: "Most-bought reading",
      };
  }
}

export function hindiCopyFor(
  planId: SEOReportPlanId,
  reason: PrimaryReportReason,
  doshaName?: string
): { headline: string; subhead: string; reasonChip: string } {
  const severityNote = doshaName ? `Aapki kundali me ${doshaName} dikh raha hai. ` : "";
  switch (reason) {
    case "next_report_param":
      return {
        headline: HINDI_NEXT_REPORT_HEADLINES[planId] ?? "Report ready hai",
        subhead:
          "Kundali ban gayi hai. Jo report aap padhne aaye the, woh ek tap door hai - payment ke baad PDF.",
        reasonChip: "Yahin se continue karein",
      };
    case "entry_intent":
      return {
        headline: HINDI_NEXT_REPORT_HEADLINES[planId] ?? "Aapke sawaal se matched report",
        subhead:
          "Kundali ban gayi hai. Jo jawaab aap dhoondh rahe the, yeh report usi ke liye hai - payment ke baad PDF.",
        reasonChip: "Aapke sawaal ke liye",
      };
    case "chat_concern":
      return {
        headline: HINDI_NEXT_REPORT_HEADLINES[planId] ?? "Aapke sawaal se matched report",
        subhead:
          "Jo sawaal aapne abhi poocha, yeh report usi ke liye hai - poora chart read, payment ke baad PDF.",
        reasonChip: "Aapke sawaal ke liye",
      };
    case "dosha_mangal":
      return {
        headline: "Mangal Dosh ka sahi upay - aapki kundali se",
        subhead: `${severityNote}Mangal kitna bhaari hai, cancel ho raha hai ya nahi, aur kaunsa upay sahi hai - sab aapke graha ke hisaab se.`,
        reasonChip: "Aapki kundali ke liye",
      };
    case "dosha_pitra":
      return {
        headline: "Pitra Dosh ka asar aur upay - aapki kundali se",
        subhead: `${severityNote}Surya, 9th house, Rahu-Ketu aur purvajon ka upay - sahi kram me, aapki kundali se.`,
        reasonChip: "Aapki kundali ke liye",
      };
    case "dosha_sade_sati":
      return {
        headline: "Sade Sati ka time aur Shani upay - aapki kundali se",
        subhead: `${severityNote}Sade Sati ka phase, aapka Shani, abhi ki mahadasha aur safe Shani upay - sab ek saath.`,
        reasonChip: "Aapki kundali ke liye",
      };
    case "dosha_kaal_sarp":
      return {
        headline: "Kaal Sarp Dosh ka sahi upay - aapki kundali se",
        subhead: `${severityNote}Kaal Sarp kitna bhaari hai, cancel ho raha hai ya nahi, aur upay ka sahi kram - shanti se pehle.`,
        reasonChip: "Aapki kundali ke liye",
      };
    case "default_brihat":
    default:
      return {
        headline: "Brihat Kundali Report - full chart read",
        subhead:
          "12 house, mahadasha + antardasha, yogas, dosha aur graha-matched upay ko readable PDF me paayein.",
        reasonChip: "Most-bought reading",
      };
  }
}

export function secondaryPlanCopy(planId: SEOReportPlanId): { headline: string; subhead: string } {
  switch (planId) {
    case PLAN_ID.brihat_kundali:
      return {
        headline: `Brihat Kundali — ₹${PLAN_DEFAULTS[planId].priceInr}`,
        subhead: "Full chart read with mahadasha, yogas, doshas and matched upay.",
      };
    case PLAN_ID.marriage_report:
      return {
        headline: `Marriage Report — ₹${PLAN_DEFAULTS[planId].priceInr}`,
        subhead: "7th house, Mangal, Kundali Milan and marriage-timing dasha.",
      };
    case PLAN_ID.career_report:
      return {
        headline: `Career Report — ₹${PLAN_DEFAULTS[planId].priceInr}`,
        subhead: "10th house, Saturn, current mahadasha — when the window opens.",
      };
    case PLAN_ID.annual_forecast:
      return {
        headline: `Annual Forecast — ₹${PLAN_DEFAULTS[planId].priceInr}`,
        subhead: "Varshphal: 12-month month-by-month read with auspicious dates.",
      };
    case PLAN_ID.mahadasha_forecast:
      return {
        headline: `Mahadasha Forecast — ₹${PLAN_DEFAULTS[planId].priceInr}`,
        subhead: "Current mahadasha, antardasha transitions, month-level timing and upay.",
      };
    case PLAN_ID.sade_sati:
      return {
        headline: `Sade Sati Report — ₹${PLAN_DEFAULTS[planId].priceInr}`,
        subhead: "Shani phase, natal Saturn, current dasha and chart-specific upay.",
      };
    case PLAN_ID.pitra_dosh:
      return {
        headline: `Pitra Dosh Report — ₹${PLAN_DEFAULTS[planId].priceInr}`,
        subhead: "9th house, Sun, Rahu/Ketu, cancellation and ancestral upay.",
      };
    case PLAN_ID.muhurat_report:
      return {
        headline: `Muhurat Report — ₹${PLAN_DEFAULTS[planId].priceInr}`,
        subhead: "Purpose-specific timing, avoid windows, sankalp and ritual checklist.",
      };
    case PLAN_ID.dosha_remedy_plan:
      return {
        headline: `Dosha Remedy Plan — ₹${PLAN_DEFAULTS[planId].priceInr}`,
        subhead: "Severity, cancellation, safe upay and matched remedy guidance.",
      };
    default:
      return { headline: "Personalised Report", subhead: "Built from your birth chart." };
  }
}

/**
 * The detected-signal sentence for the chat-reading subhead — the same
 * "Your chart shows X." proof line the kundali-result copy (`copyFor`
 * severityNote) converts with. `doshaName` is the ENGINE's `name_en` (rule 24 —
 * a chart fact, never an LLM literal); absent → empty, the copy stays as-is.
 * Kept Roman-noun in both langs per the severityNote convention (rule 21).
 */
function detectedSignalNote(doshaName: string | undefined, hi: boolean): string {
  const name = doshaName?.trim();
  if (!name) return "";
  return hi ? `Aapki kundali me ${name} dikh raha hai. ` : `Your chart shows ${name}. `;
}

/** Plans whose chat-reading subhead should carry the detected-dosha proof line.
 * Intent-picked plans (career/marriage/annual/…) keep pure message-match — a
 * dosha note there would compete with the intent the seeker arrived with
 * (rule 40). */
const DOSHA_NOTE_PLAN_IDS = new Set<SEOReportPlanId>([
  PLAN_ID.dosha_remedy_plan,
  PLAN_ID.sade_sati,
  PLAN_ID.pitra_dosh,
  PLAN_ID.brihat_kundali,
]);

function chatSignalNoteFor(
  planId: SEOReportPlanId,
  doshaName: string | undefined,
  hi: boolean
): string {
  return DOSHA_NOTE_PLAN_IDS.has(planId) ? detectedSignalNote(doshaName, hi) : "";
}

/**
 * Copy for the dedicated chat-reading value moment. The generic post-kundali
 * report copy works on a result page, but the chat card appears after the seeker
 * already received an answer. Lead with continuity: the report explains the
 * chart pattern behind this answer, then the upay/timing inside it.
 *
 * `doshaName` (engine `name_en` of the strongest present dosha) binds the card
 * to THIS seeker's detected signal: dosha-matched plans (and the default brihat)
 * open the subhead with "Your chart shows {name}." — the proof line the
 * kundali-result placement converts with — instead of a signal-free generic.
 */
export function chatReadingValueCopy(
  planId: SEOReportPlanId,
  reason: PrimaryReportReason,
  language = "en",
  doshaName?: string
): { headline: string; subhead: string } {
  if (language === "hi") return hindiChatReadingValueCopy(planId, reason, doshaName);
  const copy = englishChatReadingValueCopy(planId, reason);
  const note = chatSignalNoteFor(planId, doshaName, false);
  return note ? { ...copy, subhead: `${note}${copy.subhead}` } : copy;
}

function englishChatReadingValueCopy(
  planId: SEOReportPlanId,
  reason: PrimaryReportReason
): { headline: string; subhead: string } {
  switch (planId) {
    case PLAN_ID.career_report:
      return {
        headline: "See the career timing behind this answer",
        subhead:
          "Your report reads the 10th house, D10, dasha timeline, wealth yogas, and the upay that fit this chart.",
      };
    case PLAN_ID.marriage_report:
      return {
        headline: "See the marriage pattern behind this answer",
        subhead:
          "Your report reads compatibility, Mangal influence, timing dasha, and the practical upay before you decide.",
      };
    case PLAN_ID.annual_forecast:
      return {
        headline: "Turn this answer into your 12-month forecast",
        subhead:
          "See the year through dasha, transit pressure, monthly windows, and chart-matched upay in one private reading.",
      };
    case PLAN_ID.mahadasha_forecast:
      return {
        headline: "See what your current dasha is trying to show",
        subhead:
          "Your report maps the mahadasha, antardasha shifts, pressure points, and upay behind the question you asked.",
      };
    case PLAN_ID.muhurat_report:
      return {
        headline: "Pick the right muhurat from this chart",
        subhead:
          "Get purpose-specific timing, avoid windows, sankalp guidance, and the checklist matched to your birth details.",
      };
    case PLAN_ID.sade_sati:
      return {
        headline: "See the Shani pressure behind this answer",
        subhead:
          "Your Sade Sati report reads natal Shani, the current phase, dasha support, pressure areas, and safe upay.",
      };
    case PLAN_ID.pitra_dosh:
      return {
        headline: "See the ancestral pattern behind this answer",
        subhead:
          "Your report reads the Sun, 9th house, Rahu/Ketu links, cancellation, and the upay sequence that fits.",
      };
    case PLAN_ID.dosha_remedy_plan:
      return {
        headline:
          reason === "dosha_kaal_sarp"
            ? "See the Kaal Sarp chain behind this answer"
            : "See the dosha pattern behind this answer",
        subhead:
          "Your remedy plan checks severity, cancellation, and the safe upay sequence before any remedy is overdone.",
      };
    case PLAN_ID.brihat_kundali:
    default:
      return {
        headline: "Your chat answer is only the first layer",
        subhead:
          "See the 12 bhav, dasha timeline, yogas, doshas, and upay behind this answer in your full chart reading.",
      };
  }
}

function hindiChatReadingValueCopy(
  planId: SEOReportPlanId,
  reason: PrimaryReportReason,
  doshaName?: string
): { headline: string; subhead: string } {
  const copy = hindiChatReadingValueCopyBase(planId, reason);
  const note = chatSignalNoteFor(planId, doshaName, true);
  return note ? { ...copy, subhead: `${note}${copy.subhead}` } : copy;
}

function hindiChatReadingValueCopyBase(
  planId: SEOReportPlanId,
  reason: PrimaryReportReason
): { headline: string; subhead: string } {
  switch (planId) {
    case PLAN_ID.career_report:
      return {
        headline: "Is answer ke peeche career timing dekhein",
        subhead:
          "Report 10th house, D10, dasha timeline, dhan yoga aur is chart ke sahi upay read karti hai.",
      };
    case PLAN_ID.marriage_report:
      return {
        headline: "Is answer ke peeche marriage pattern dekhein",
        subhead:
          "Report compatibility, Mangal influence, timing dasha aur practical upay ko decision se pehle read karti hai.",
      };
    case PLAN_ID.annual_forecast:
      return {
        headline: "Is answer ko 12-month forecast mein badlein",
        subhead:
          "Dasha, transit pressure, monthly windows aur chart-matched upay ko ek private reading mein dekhein.",
      };
    case PLAN_ID.mahadasha_forecast:
      return {
        headline: "Aapki current dasha kya dikhana chahti hai",
        subhead:
          "Report mahadasha, antardasha shifts, pressure points aur aapke sawaal ke peeche ka upay map karti hai.",
      };
    case PLAN_ID.muhurat_report:
      return {
        headline: "Is chart se sahi muhurat choose karein",
        subhead:
          "Purpose-specific timing, avoid windows, sankalp guidance aur checklist birth details se matched.",
      };
    case PLAN_ID.sade_sati:
      return {
        headline: "Is answer ke peeche Shani pressure dekhein",
        subhead:
          "Sade Sati report natal Shani, current phase, dasha support, pressure areas aur safe upay read karti hai.",
      };
    case PLAN_ID.pitra_dosh:
      return {
        headline: "Is answer ke peeche ancestral pattern dekhein",
        subhead:
          "Report Surya, 9th house, Rahu/Ketu links, cancellation aur fit upay sequence read karti hai.",
      };
    case PLAN_ID.dosha_remedy_plan:
      return {
        headline:
          reason === "dosha_kaal_sarp"
            ? "Is answer ke peeche Kaal Sarp chain dekhein"
            : "Is answer ke peeche dosha pattern dekhein",
        subhead:
          "Remedy plan severity, cancellation aur safe upay sequence check karta hai - remedy overdo karne se pehle.",
      };
    case PLAN_ID.brihat_kundali:
    default:
      return {
        headline: "Aapka chat answer sirf pehli layer hai",
        subhead:
          "Is answer ke peeche 12 bhav, dasha timeline, yogas, dosha aur upay full chart reading mein dekhein.",
      };
  }
}

/**
 * Pain-first (rule 22) copy for the chat-reading offer A/B `variant` arm. It
 * overrides ONLY the offer headline + subhead — planId, price and the Razorpay
 * checkout are unchanged, so the test is a pure copy change on the same offer
 * (zero money-path risk beyond wording). The control arm keeps the feature-first
 * copy from {@link buildPick}.
 *
 * Framing follows rule 22's [pain as "why does this keep happening?"] → [Vedic
 * explanation] → [upay] formula, keyed to the report `reason` (the seeker's
 * dominant dosha/intent). It is evergreen prose — no dates, positions or scores
 * (rule 24) — and uses verified Jyotish vocabulary (rule 21); the hi arm is
 * Hinglish (single Indian noun + English action word).
 */
export function painFirstVariantCopy(
  reason: PrimaryReportReason,
  language = "en"
): { headline: string; subhead: string } {
  type Copy = { headline: string; subhead: string };
  const en: Record<PrimaryReportReason, Copy> = {
    dosha_mangal: {
      headline: "Why do you lose your temper even when you don't want to?",
      subhead:
        "Mangal in your chart creates exactly this pattern. Your reading names it — and the upay that cools it.",
    },
    dosha_pitra: {
      headline: "Why does bad luck keep following you, no matter what you do?",
      subhead:
        "Pitra Dosh — ancestral karma in your 9th house — repeats this. Your reading names the pattern and the upay that clears it.",
    },
    dosha_sade_sati: {
      headline: "Why does it feel like something keeps holding you back?",
      subhead:
        "Shani's Sade Sati is pressing your chart. Your reading shows the timing — and which upay actually fit Shani.",
    },
    dosha_kaal_sarp: {
      headline: "Why does life feel stuck at a ceiling you can't break?",
      subhead:
        "Kaal Sarp Dosh creates exactly this ceiling. Your reading names the chain — and the upay sequence that lifts it.",
    },
    default_brihat: {
      headline: "Why does the same problem keep returning in your life?",
      subhead:
        "Your chart names the pattern most people never see — and the upay that breaks it. Full reading inside.",
    },
    next_report_param: {
      headline: "The answer you came back for is in your chart.",
      subhead:
        "Pick up your reading — the pattern behind the problem, and the upay that addresses it.",
    },
    entry_intent: {
      headline: "The question that brought you here has a chart answer.",
      subhead:
        "Your reading names the pattern behind what you came to check — and the upay that addresses it.",
    },
    chat_concern: {
      headline: "The question you just asked has a chart answer.",
      subhead:
        "Your reading names the pattern behind what you asked — and the upay that addresses it.",
    },
  };
  const hi: Record<PrimaryReportReason, Copy> = {
    dosha_mangal: {
      headline: "Gussa control kyun nahi hota, chaahe aap chaahein bhi?",
      subhead:
        "Aapki kundali ka Mangal yahi pattern banata hai. Reading ise naam deti hai — aur wo upay jo ise shaant kare.",
    },
    dosha_pitra: {
      headline: "Sab sahi karne par bhi bure din peechha kyun nahi chhodte?",
      subhead:
        "Pitra Dosh — 9th house ka ancestral karma — yahi dohrata hai. Reading pattern aur use clear karne wala upay batati hai.",
    },
    dosha_sade_sati: {
      headline: "Aisa kyun lagta hai jaise koi cheez aapko rok rahi hai?",
      subhead:
        "Shani ki Sade Sati aapki kundali par hai. Reading timing dikhati hai — aur kaun se upay Shani ke liye sahi hain.",
    },
    dosha_kaal_sarp: {
      headline: "Zindagi ek ceiling par stuck kyun mehsoos hoti hai?",
      subhead:
        "Kaal Sarp Dosh yahi ceiling banata hai. Reading poori chain ko naam deti hai — aur use uthane wala upay sequence.",
    },
    default_brihat: {
      headline: "Wahi problem baar-baar kyun lautti hai aapki zindagi mein?",
      subhead:
        "Aapki kundali wo pattern batati hai jo zyaadatar log dekh nahi paate — aur wo upay jo ise tode. Poori reading andar.",
    },
    next_report_param: {
      headline: "Jis jawaab ke liye aap laute, wo aapki kundali mein hai.",
      subhead:
        "Apni reading wahin se shuru karein — problem ke peeche ka pattern, aur use address karne wala upay.",
    },
    entry_intent: {
      headline: "Jo sawaal aapko yahan laya, uska jawaab kundali mein hai.",
      subhead:
        "Reading us pattern ko naam deti hai jo aap check karne aaye — aur use address karne wala upay.",
    },
    chat_concern: {
      headline: "Jo sawaal aapne abhi poocha, uska jawaab kundali mein hai.",
      subhead:
        "Reading us pattern ko naam deti hai jo aapne poocha — aur use address karne wala upay.",
    },
  };
  return (language === "hi" ? hi : en)[reason];
}

// ── Answer-continuation deliverables (rule 39/40/43 + the 2026 message-match /
// concrete-deliverables conversion standard). The 3 deliverable rows come from
// governed data (catalog.report_deliverables → domain-data); here we only
// RESOLVE the one render-time token they carry. ──────────────────────────────

/** A resolved deliverable row ready to render (no placeholders left). */
export interface ResolvedDeliverable {
  /** "Sections", "Delivery", "For your chart" — the row's kind. */
  slot: "sections" | "delivery" | "personalized";
  text: string;
}

/**
 * The seeker-language phrase that fills the `{{chart_reason}}` placeholder in a
 * plan's `personalized` deliverable. This is rule-24-safe: it maps the
 * engine-detected reason (a closed enum, NOT an LLM output) to evergreen prose —
 * never a date, score, or position. `doshaName` (the engine's `name_en`, e.g.
 * "Mangal Dosh") personalises the dosha branches further when present.
 */
export function resolveChartReason(
  reason: PrimaryReportReason,
  language: string,
  doshaName?: string
): string {
  const hi = language === "hi";
  switch (reason) {
    case "dosha_mangal":
      return doshaName ?? "Mangal Dosh";
    case "dosha_pitra":
      return doshaName ?? "Pitra Dosh";
    case "dosha_sade_sati":
      return doshaName ?? "Sade Sati";
    case "dosha_kaal_sarp":
      return doshaName ?? "Kaal Sarp Dosh";
    case "next_report_param":
      return hi ? "aapke sawaal" : "the question you came back for";
    case "entry_intent":
      return hi ? "jo sawaal aapko yahan laya" : "the question that brought you here";
    case "chat_concern":
      return hi ? "jo sawaal aapne abhi poocha" : "the question you just asked";
    case "default_brihat":
    default:
      return hi ? "aapke poore chart" : "your full chart";
  }
}

const DELIVERABLE_TOKEN = "{{chart_reason}}";

/**
 * Resolve a plan's governed deliverables triplet into 3 ready-to-render rows in
 * the seeker's language, substituting the `{{chart_reason}}` token from the
 * engine-detected reason. Returns exactly 3 rows in a stable order (sections →
 * delivery → personalized) so the shared continuation card renders a consistent
 * shape across both placements.
 */
export function resolveDeliverables(
  deliverables: ReportDeliverables,
  reason: PrimaryReportReason,
  language: string,
  doshaName?: string
): ResolvedDeliverable[] {
  const lang = language === "hi" ? "hi" : "en";
  const chartReason = resolveChartReason(reason, language, doshaName);
  return (["sections", "delivery", "personalized"] as const).map((slot) => ({
    slot,
    text: deliverables[slot][lang].split(DELIVERABLE_TOKEN).join(chartReason),
  }));
}
